import { ImageOff } from "lucide-react";
import { useEffect, useRef, useState, type ImgHTMLAttributes } from "react";

import { acquireSlot } from "@/lib/imageQueue";

/** Паузы перед повторами превью, мс: шлюз успевает отпустить. */
const RETRY_MS = [1000, 2000, 4000, 8000, 8000, 8000];
/** Начинать загрузку чуть раньше, чем плитка въедет в экран. */
const NEAR_PX = "600px";

/**
 * Картинка сетки: грузится, только когда плитка у экрана, и через общую
 * очередь (`acquireSlot`) — не больше нескольких запросов разом. Отказ шлюза
 * переживает: ждёт и просит заново (метка `?r=` — иначе браузер не повторит
 * тот же адрес). Кончились попытки — значок вместо битой картинки.
 */
export function RetryImg({ src, alt, ...rest }: { src: string } & Omit<ImgHTMLAttributes<HTMLImageElement>, "src">) {
  const img = useRef<HTMLImageElement>(null);
  const [near, setNear] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [granted, setGranted] = useState(false);
  const [cooling, setCooling] = useState(false);
  const [failed, setFailed] = useState(false);
  const release = useRef<(() => void) | null>(null);

  useEffect(() => {
    const el = img.current;
    if (!el || near) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      // Прокручивается `main` (или окно выбора `dialog`), а не окно браузера: запас считаем от него.
      { root: el.closest("dialog, main"), rootMargin: NEAR_PX },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [near]);

  // Попытка = место в очереди; место отдаём на load/error или при уходе с экрана.
  useEffect(() => {
    if (!near || granted || cooling || failed) return;
    let alive = true;
    void acquireSlot().then((free) => {
      if (!alive) return free();
      release.current = free;
      setGranted(true);
    });
    return () => {
      alive = false;
    };
  }, [near, granted, cooling, failed]);
  useEffect(() => () => release.current?.(), []);

  const settle = () => {
    release.current?.();
    release.current = null;
  };
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  if (failed)
    return (
      <span className="absolute inset-0 grid place-items-center text-[12px] text-fg-faint">
        <span className="grid justify-items-center gap-1">
          <ImageOff size={18} />
          не загрузилась
        </span>
      </span>
    );
  return (
    <img
      {...rest}
      ref={img}
      // Ленивость — наша (очередь + IntersectionObserver): с браузерной
      // `lazy` картинка вне экрана не грузится и держит место в очереди вечно.
      loading="eager"
      alt={granted ? alt : ""}
      src={granted ? (attempt ? `${src}?r=${attempt}` : src) : undefined}
      onLoad={settle}
      onError={() => {
        if (!granted) return;
        settle();
        setGranted(false);
        const wait = RETRY_MS[attempt];
        if (wait === undefined) return setFailed(true);
        setCooling(true);
        timer.current = window.setTimeout(() => {
          setAttempt(attempt + 1);
          setCooling(false);
        }, wait + Math.random() * 500);
      }}
    />
  );
}
